import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../api.ts';
import { useMuvelet } from '../felhasznalo-context.ts';
import { letolt } from '../letoltes.ts';
import { ft, szam } from '../formazas.ts';
import { Mezo } from './Mezo.tsx';

/**
 * Szűrt mérés és kimutatás. (3.0 / 5.9–5.10)
 *
 * A szűrt mérést az átvevő utólag küldi meg: kézzel is beírható, de
 * jellemzően fájlból jön. A kimutatás nyolc kérdésre felel, sávokkal —
 * ugyanaz, amit a demó mutat.
 */

interface SzurtSor {
  id: number;
  szam: string;
  kelt: string;
  etterem_nev: string | null;
  sofor: string | null;
  szallitoi_kg: number | null;
  hiteles_kg: number | null;
  szurt_kg: number | null;
  veszteseg_kg: number | null;
  veszteseg_szazalek: number | null;
  tisztasag: number | null;
}

interface ElonezetSor {
  fajl_sor: number;
  szam: string;
  kg: number | null;
  sors: string;
  etterem_nev?: string | null;
  regi_szurt_kg?: number | null;
}

const SORS_NEVEK: Record<string, string> = {
  uj: 'beírjuk',
  felulir: 'felülírja a meglévőt',
  nincs_ilyen: 'nincs ilyen sorszám',
  nem_kiadva: 'még nincs átadva',
  nem_hso: 'nem HSO tétel',
  nincs_suly: 'nincs súly a sorban',
};

export function Szurt({ onHiba, onUzenet }: { onHiba: (h: string) => void; onUzenet: (u: string) => void }) {
  const rogzithet = useMuvelet('szurt_rogzit');
  const [sorok, setSorok] = useState<SzurtSor[]>([]);
  const [csakHianyzo, setCsakHianyzo] = useState(false);
  const [elonezet, setElonezet] = useState<{ sorok: ElonezetSor[]; osszegzes: Record<string, number> } | null>(
    null,
  );
  const [fajl, setFajl] = useState<File | null>(null);
  const [felulir, setFelulir] = useState(false);
  const [munkaban, setMunkaban] = useState(false);
  const mezoRef = useRef<HTMLInputElement>(null);

  const betolt = useCallback(async () => {
    try {
      const v = await api.get<{ sorok: SzurtSor[] }>(
        `/api/szallitolevel/szurt?csak_hianyzo=${csakHianyzo ? '1' : '0'}`,
      );
      setSorok(v.sorok);
    } catch (h) {
      onHiba(h instanceof Error ? h.message : 'A szűrt méréseket nem sikerült betölteni.');
    }
  }, [csakHianyzo, onHiba]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  async function fajltNez(valasztott: File) {
    setMunkaban(true);
    try {
      const tartalom = await valasztott.arrayBuffer();
      const v = await fetch('/api/szallitolevel/szurt/elonezet', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-Fajlnev': encodeURIComponent(valasztott.name),
        },
        body: tartalom,
      });
      const test = (await v.json()) as { hiba?: string; sorok?: ElonezetSor[]; osszegzes?: Record<string, number> };
      if (!v.ok || test.hiba) throw new Error(test.hiba ?? 'A fájlt nem sikerült beolvasni.');
      setElonezet({ sorok: test.sorok ?? [], osszegzes: test.osszegzes ?? {} });
      setFajl(valasztott);
    } catch (h) {
      onHiba(h instanceof Error ? h.message : 'A fájlt nem sikerült beolvasni.');
    } finally {
      setMunkaban(false);
    }
  }

  async function betolteni() {
    if (!fajl) return;
    setMunkaban(true);
    try {
      const tartalom = await fajl.arrayBuffer();
      const v = await fetch('/api/szallitolevel/szurt/betoltes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-Fajlnev': encodeURIComponent(fajl.name),
          'X-Felulir': felulir ? '1' : '0',
        },
        body: tartalom,
      });
      const test = (await v.json()) as {
        hiba?: string;
        beirt?: number;
        felulirt?: number;
        kihagyott?: number;
        hibak?: string[];
      };
      if (!v.ok || test.hiba) throw new Error(test.hiba ?? 'A betöltés nem sikerült.');
      onUzenet(
        `${test.beirt ?? 0} új mérés beírva` +
          (test.felulirt ? `, ${test.felulirt} felülírva` : '') +
          (test.kihagyott ? `, ${test.kihagyott} kihagyva` : '') +
          '.' +
          (test.hibak?.length ? ` Hibák: ${test.hibak.slice(0, 3).join(' · ')}` : ''),
      );
      setElonezet(null);
      setFajl(null);
      if (mezoRef.current) mezoRef.current.value = '';
      await betolt();
    } catch (h) {
      onHiba(h instanceof Error ? h.message : 'A betöltés nem sikerült.');
    } finally {
      setMunkaban(false);
    }
  }

  return (
    <>
      <div className="actbar">
        <label className="kapcs">
          <input type="checkbox" checked={csakHianyzo} onChange={(e) => setCsakHianyzo(e.target.checked)} />
          Csak amire még nincs szűrt mérés
        </label>
        {rogzithet && (
          <>
            <label className="btn sm" style={{ cursor: 'pointer', margin: 0 }}>
              Fájl betöltése
              <input
                ref={mezoRef}
                type="file"
                accept=".xlsx,.xlsm,.csv,.txt"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void fajltNez(f);
                }}
              />
            </label>
            <span className="hint">
              Elég, ha a fájlban van egy szállítólevél-sorszám és egy súly oszlop. Excel és CSV is jó.
            </span>
          </>
        )}
      </div>

      {elonezet && (
        <div className="npform">
          <h4>Előnézet — {fajl?.name}</h4>
          <div className="c" style={{ marginBottom: 8 }}>
            {Object.entries(elonezet.osszegzes)
              .filter(([, n]) => n > 0)
              .map(([k, n]) => `${n} ${SORS_NEVEK[k] ?? k}`)
              .join(' · ')}
          </div>
          <div className="matrix" style={{ maxHeight: 260, overflow: 'auto' }}>
            <table className="tt2">
              <thead>
                <tr>
                  <th className="n">Sor</th>
                  <th>Sorszám</th>
                  <th>Partner</th>
                  <th className="n">Szűrt (kg)</th>
                  <th className="n">Eddigi</th>
                  <th>Mi lesz vele</th>
                </tr>
              </thead>
              <tbody>
                {elonezet.sorok.map((s) => (
                  <tr key={`${s.fajl_sor}-${s.szam}`} className={s.sors === 'uj' ? undefined : 'nul'}>
                    <td className="calc2">{s.fajl_sor}</td>
                    <td className="calc2">{s.szam}</td>
                    <td>{s.etterem_nev ?? ''}</td>
                    <td className="calc2">{s.kg === null ? '' : szam(s.kg, 0)}</td>
                    <td className="calc2">{s.regi_szurt_kg === null ? '' : szam(s.regi_szurt_kg ?? 0, 0)}</td>
                    <td>{SORS_NEVEK[s.sors] ?? s.sors}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <label className="kapcs">
            <input type="checkbox" checked={felulir} onChange={(e) => setFelulir(e.target.checked)} />
            A már meglévő szűrt mérést is írja felül
          </label>
          <div className="btns">
            <button className="btn gold sm" disabled={munkaban} onClick={() => void betolteni()}>
              {munkaban ? 'Betöltés…' : 'Betöltés'}
            </button>
            <button
              className="btn sm"
              onClick={() => {
                setElonezet(null);
                setFajl(null);
                if (mezoRef.current) mezoRef.current.value = '';
              }}
            >
              Mégse
            </button>
          </div>
        </div>
      )}

      <div className="matrix" style={{ maxHeight: 560, overflow: 'auto' }}>
        <table className="tt2">
          <thead>
            <tr>
              <th>Sorszám</th>
              <th className="n">Kelt</th>
              <th>Partner</th>
              <th className="n">Szállítói</th>
              <th className="n">Hiteles</th>
              <th className="n">Szűrt</th>
              <th className="n">Veszteség</th>
              <th className="n">Tisztaság</th>
              <th>Sofőr</th>
            </tr>
          </thead>
          <tbody>
            {sorok.map((s) => (
              <tr key={s.id}>
                <td className="calc2" style={{ whiteSpace: 'nowrap' }}>
                  {s.szam}
                </td>
                <td className="calc2">{s.kelt}</td>
                <td className="pn">{s.etterem_nev}</td>
                <td className="calc2">{s.szallitoi_kg ? `${szam(s.szallitoi_kg, 0)} kg` : ''}</td>
                <td className="calc2">{s.hiteles_kg ? `${szam(s.hiteles_kg, 0)} kg` : ''}</td>
                <td className="c" style={{ width: 110 }}>
                  <Mezo
                    ertek={s.szurt_kg}
                    fajta="szam"
                    osztaly="c"
                    tiltva={!rogzithet}
                    onMent={async (uj) => {
                      await api.patch(`/api/szallitolevel/${s.id}/szurt`, { kg: uj });
                      await betolt();
                    }}
                  />
                </td>
                <td className="calc2">
                  {s.veszteseg_kg === null
                    ? ''
                    : `${szam(s.veszteseg_kg, 0)} kg · ${szam(s.veszteseg_szazalek ?? 0, 1)}%`}
                </td>
                <td className="calc2">
                  {s.tisztasag === null ? (
                    ''
                  ) : (
                    <span className={s.tisztasag < 85 ? 'bad2' : undefined}>{szam(s.tisztasag, 1)}%</span>
                  )}
                </td>
                <td>{s.sofor}</td>
              </tr>
            ))}
            {sorok.length === 0 && (
              <tr>
                <td colSpan={9} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  Még nincs átadott HSO tétel.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */

interface SavSor {
  nev: string;
  etterem_id: number | null;
  ertek: number;
  darab?: number;
}

interface Kimutatas {
  idoszak: string;
  osszesito: {
    /** A hiteles mérés szerint — ezen az alapon megy a kiadás is. */
    hso_kg: number;
    /** Amit a sofőrök mértek; a kettő különbsége a mérlegeltérés. */
    hso_sofor_kg: number;
    hso_darab: number;
    mo_hordo: number;
    mo_kg: number;
    partnerek: number;
    meghiusult: number;
    meghiusult_szazalek: number;
  };
  top_hso: SavSor[];
  top_mo: SavSor[];
  tisztasag: SavSor[];
  elteres: SavSor[];
  /** Sofőrönként, fajtánként külön — a moslék súlya nem keveredhet bele. */
  soforok: Array<{
    sofor: string;
    okmany: number;
    hso_kg: number;
    hso_hordo: number;
    mo_kg: number;
    mo_hordo: number;
  }>;
  meghiusulas: SavSor[];
  leggyakoribb_ok: string | null;
  havi: Array<{ honap: string; hso_kg: number; mo_hordo: number }>;
  potlando: SavSor[];
}

const IDOSZAKOK: Array<[string, string]> = [
  ['30nap', '30 nap'],
  ['3honap', '3 hónap'],
  ['ev', '1 év'],
  ['mind', 'mind'],
];

function Savok({ sorok, egyseg, max }: { sorok: SavSor[]; egyseg: string; max?: number }) {
  if (sorok.length === 0) return <div className="kimures">Nincs adat az időszakban.</div>;
  const mx = max ?? Math.max(...sorok.map((s) => s.ertek), 1);
  return (
    <>
      {sorok.map((s, i) => (
        <div className="kimsor" key={`${s.etterem_id}-${s.nev}-${i}`}>
          <span className="kimh">{i + 1}.</span>
          <span className="kimn">{s.nev}</span>
          <span className="kimb">
            <i style={{ width: `${Math.max(2, (s.ertek / mx) * 100)}%` }} />
          </span>
          <span className="kimv">
            {s.ertek % 1 ? szam(s.ertek, 1) : ft(s.ertek)}
            {egyseg}
          </span>
        </div>
      ))}
    </>
  );
}

export function SzlevKimutatas({ onHiba }: { onHiba: (h: string) => void }) {
  const letolthet = useMuvelet('export_szlev');
  const [idoszak, setIdoszak] = useState('30nap');
  const [adat, setAdat] = useState<Kimutatas | null>(null);

  useEffect(() => {
    api
      .get<Kimutatas>(`/api/szallitolevel/kimutatas?idoszak=${idoszak}`)
      .then(setAdat)
      .catch((h: Error) => onHiba(h.message));
  }, [idoszak, onHiba]);

  const o = adat?.osszesito;

  return (
    <>
      <div className="actbar">
        <div className="vsw">
          {IDOSZAKOK.map(([k, cimke]) => (
            <button key={k} className={idoszak === k ? 'on' : undefined} onClick={() => setIdoszak(k)}>
              {cimke}
            </button>
          ))}
        </div>
        {letolthet && (
          <button className="btn sm" onClick={() => letolt(`/api/export/szallitolevel.xlsx?idoszak=${idoszak}`)}>
            Excel
          </button>
        )}
        {letolthet && (
          <button
            className="btn sm"
            title="Készletnapló: melyik nap mi jött be a raktárba és mi ment ki"
            onClick={() => letolt('/api/export/raktarkeszlet.xlsx')}
          >
            Raktárkészlet
          </button>
        )}
      </div>

      <div className="kimkart4">
        {/*
          * A hiteles mérés az irányadó — a kiadási okmány is ezen az alapon
          * számol. A sofőr mérése mellette áll, hogy a kettő különbsége
          * látszódjon. (2026-10-13, a megrendelő jelzése)
          */}
        <div>
          <span>HSO begyűjtve</span>
          <b>{ft(o?.hso_kg ?? 0)} kg</b>
          <em>
            {ft(o?.hso_darab ?? 0)} szállítólevél · hiteles mérés szerint
            {(o?.hso_sofor_kg ?? 0) > 0 && (o?.hso_sofor_kg ?? 0) !== (o?.hso_kg ?? 0)
              ? ` · sofőr szerint ${ft(o!.hso_sofor_kg)} kg`
              : ''}
          </em>
        </div>
        {/*
          * A moslékhoz a gyakorlatban nem írnak súlyt — ilyenkor nem írunk
          * ki „0 kg"-ot, mert az adatnak látszik. (2026-10-13)
          */}
        <div>
          <span>Moslék begyűjtve</span>
          <b>{ft(o?.mo_hordo ?? 0)} hordó</b>
          <em>
            {(o?.mo_kg ?? 0) > 0 ? `${ft(o!.mo_kg)} kg összesen` : 'súly nincs rögzítve'}
          </em>
        </div>
        <div>
          <span>Partner</span>
          <b>{ft(o?.partnerek ?? 0)}</b>
          <em>akitől gyűjtöttünk</em>
        </div>
        <div>
          <span>Meghiúsult</span>
          <b>{ft(o?.meghiusult ?? 0)}</b>
          <em>{szam(o?.meghiusult_szazalek ?? 0, 1)}% a látogatásokból</em>
        </div>
      </div>

      <div className="kimgrid">
        <div className="kimdoboz">
          <h4>Top 10 — HSO mennyiség</h4>
          <Savok sorok={adat?.top_hso ?? []} egyseg=" kg" />
        </div>
        <div className="kimdoboz">
          <h4>Top 10 — moslék hordó</h4>
          <Savok sorok={adat?.top_mo ?? []} egyseg=" db" />
        </div>
        <div className="kimdoboz">
          <h4>Legszennyezettebb olaj</h4>
          <div className="kimmagy">Szűrés után megmaradt arány — alul a legrosszabb.</div>
          <Savok sorok={adat?.tisztasag ?? []} egyseg="%" max={100} />
        </div>
        <div className="kimdoboz">
          <h4>Legnagyobb mérési eltérés</h4>
          <div className="kimmagy">A szállítói és a hiteles súly különbsége, százalékban.</div>
          <Savok sorok={adat?.elteres ?? []} egyseg="%" />
        </div>
        <div className="kimdoboz">
          <h4>Sofőrönként</h4>
          <table className="kimt">
            <tbody>
              {/*
                * Fajtánként külön oszlop. Eddig egyetlen „Tömeg" állt itt, és
                * az a moslék súlyát is beszámolta — ettől nem jött ki a HSO
                * összesítővel. (2026-10-13, a megrendelő jelzése)
                */}
              <tr>
                <th>Sofőr</th>
                <th className="r">Okmány</th>
                <th className="r">HSO</th>
                <th className="r">HSO hordó</th>
                <th className="r">Moslék</th>
                <th className="r">MO hordó</th>
              </tr>
              {(adat?.soforok ?? []).map((s) => (
                <tr key={s.sofor}>
                  <td>{s.sofor}</td>
                  <td className="r">{s.okmany}</td>
                  <td className="r">{s.hso_kg ? `${ft(s.hso_kg)} kg` : '·'}</td>
                  <td className="r">{s.hso_hordo || '·'}</td>
                  <td className="r">{s.mo_kg ? `${ft(s.mo_kg)} kg` : '·'}</td>
                  <td className="r">{s.mo_hordo || '·'}</td>
                </tr>
              ))}
              {(adat?.soforok.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={6} className="kimures">
                    Nincs adat.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="kimdoboz">
          <h4>Ismétlődő meghiúsulás</h4>
          <Savok sorok={adat?.meghiusulas ?? []} egyseg="×" />
          {adat?.leggyakoribb_ok && (
            <div className="kimmagy" style={{ marginTop: 10 }}>
              Leggyakoribb ok: <b>{adat.leggyakoribb_ok}</b>
            </div>
          )}
        </div>
        <div className="kimdoboz">
          <h4>Havi bontás</h4>
          <table className="kimt">
            <tbody>
              <tr>
                <th>Hónap</th>
                <th className="r">HSO</th>
                <th className="r">Moslék</th>
              </tr>
              {(adat?.havi ?? []).map((h) => (
                <tr key={h.honap}>
                  <td>{h.honap}</td>
                  <td className="r">{ft(h.hso_kg)} kg</td>
                  <td className="r">{h.mo_hordo} hordó</td>
                </tr>
              ))}
              {(adat?.havi.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={3} className="kimures">
                    Nincs adat.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="kimdoboz">
          <h4>Pótlandó hordók</h4>
          <Savok sorok={adat?.potlando ?? []} egyseg=" db" />
        </div>
      </div>
    </>
  );
}
